import './CookiesBanner.scss';

import Parser from 'html-react-parser';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Modal } from 'rsuite';

import Button from '@/common/components/atoms/Button/Button';
import ButtonClose from '@/common/components/molecules/ButtonClose/ButtonClose';
import CheckboxField from '@/common/components/molecules/CheckboxField/CheckboxField';
import { COOKIE_SETUP, ROUTE_LEGALS } from '@/common/constants';
import withCookiesBannerLogic from '@/common/hoc/withCookiesBannerLogic';
import CookiesAnalytics from '@/common/services/Cookies.analytics';

import {
  CookiesBannerDefaultProps,
  CookiesBannerPropTypes,
} from './CoockiesBanner.proptypes';

const CookiesBanner = ({
  children,
  isOpen,
  onSubmit,
  onAcceptAll,
  onRejectAll,
}) => {
  const { t } = useTranslation();
  const BASE_URL = window.location.origin;
  const addBlockerCheckerRef = useRef();

  const [isFirstStep, setIsFirstStep] = useState(true);
  const [isSelectedPerformance, setIsSelectedPerformance] = useState(true);

  const handleClickOutside = useCallback(() => {
    if (addBlockerCheckerRef.current) {
      const isStillInDom = document.body.contains(addBlockerCheckerRef.current);
      if (!isStillInDom) {
        onRejectAll(false);
        window.location.reload();
      }
    }
  }, [onRejectAll]);

  const handleClickInside = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  useEffect(() => {
    if (
      window.navigator.brave?.isBrave.name === 'isBrave' &&
      CookiesAnalytics.getCookie(COOKIE_SETUP) !== '1'
    ) {
      onRejectAll();
    }
  }, [onRejectAll]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => document.removeEventListener('click', handleClickOutside);
  }, [handleClickOutside]);

  return (
    <Modal className="rgpd-banner" open={isOpen} data-qa-id="rgpd-banner">
      <div
        className="rgpd-banner__container"
        role="presentation"
        onClick={handleClickInside}
      >
        <div ref={addBlockerCheckerRef} />
        {isFirstStep ? (
          <div
            className="rgpd-banner__first-step step"
            data-qa-id="rgpd-banner"
            data-qa-step="first"
          >
            <div className="step__top">
              <Button onClick={onRejectAll} context="reject-all">
                {t(
                  'common:entities:common.modal.cookies_banner.first_step.refuse',
                )}
              </Button>
            </div>
            <div className="step__description">
              <Trans i18nKey="common:entities:common.modal.cookies_banner.first_step.description">
                <a href={BASE_URL + ROUTE_LEGALS}>Legals</a>
              </Trans>
            </div>
            <div className="step__actions">
              <Button
                buttonSize="large"
                context="customize"
                onClick={() => setIsFirstStep(false)}
                variant="secondary-light"
              >
                {t('common:messages:actions.customize')}
              </Button>
              <Button
                buttonSize="large"
                context="accept-all"
                onClick={onAcceptAll}
                variant="primary"
              >
                {t('common:messages:actions.accept_all')}
              </Button>
            </div>
            {children}
          </div>
        ) : (
          <div
            className="rgpd-banner__second-step step"
            data-qa-id="rgpd-banner"
            data-qa-step="second"
          >
            <div className="step__top">
              <ButtonClose
                onClick={() => setIsFirstStep(true)}
                context="back-first-step"
              />
            </div>
            <div className="step__description">
              {Parser(
                t(
                  'common:entities:common.modal.cookies_banner.second_step.description',
                ),
              )}
            </div>
            <div className="step__field field">
              <CheckboxField
                checked
                className="field__toggle"
                context="cookie-necessity"
                disabled
                id="cookie-necessity"
                labelContent={t(
                  'common:entities:common.modal.cookies_banner.second_step.fields.cookie_necessity.label',
                )}
                name="cookie-necessity"
                type="toggle"
              />
              <div className="field__description">
                {Parser(
                  t(
                    'common:entities:common.modal.cookies_banner.second_step.fields.cookie_necessity.description',
                  ),
                )}
              </div>
            </div>
            <div className="step__field field">
              <CheckboxField
                checked={isSelectedPerformance}
                className="field__toggle"
                context="cookie-performance"
                id="cookie-performance"
                labelContent={t(
                  'common:entities:common.modal.cookies_banner.second_step.fields.cookie_performance.label',
                )}
                name="cookie-performance"
                onChange={(e) => setIsSelectedPerformance(e.target.checked)}
                type="toggle"
              />
              <div className="field__description">
                {Parser(
                  t(
                    'common:entities:common.modal.cookies_banner.second_step.fields.cookie_performance.description',
                  ),
                )}
              </div>
            </div>
            <div className="step__actions">
              <Button
                buttonSize="large"
                onClick={() => onSubmit(isSelectedPerformance)}
                variant="primary"
                context="accept-save"
              >
                {t('common:messages:actions.save')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

CookiesBanner.defaultProps = {
  ...CookiesBannerDefaultProps,
};

CookiesBanner.propTypes = {
  ...CookiesBannerPropTypes,
};

export default withCookiesBannerLogic(CookiesBanner);
